import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Plus, CheckCircle, AlertCircle, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const confidenceBadge = (confidence, matchType) => {
  if (matchType === 'exact') return { cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200', label: 'Exact match' };
  if (matchType === 'alias') return { cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200', label: 'Alias match' };
  if (confidence >= 0.75) return { cls: 'bg-amber-100 text-amber-700 border border-amber-200', label: `${Math.round(confidence * 100)}% match` };
  return { cls: 'bg-[#F8EBD9] text-[#6E6258] border border-[#E7D2B8]', label: `${Math.round(confidence * 100)}% match` };
};

const confidenceIcon = (confidence, matchType) => {
  if (matchType === 'exact' || matchType === 'alias') return <CheckCircle size={14} className="text-emerald-600 shrink-0" />;
  if (confidence >= 0.75) return <AlertCircle size={14} className="text-amber-500 shrink-0" />;
  return <Info size={14} className="text-[#9C8E84] shrink-0" />;
};


// ─── Organisation Match Dialog ───

export function OrgMatchDialog({ open, onOpenChange, onSelect, organisations, setOrganisations, prefillName = '' }) {
  const [query, setQuery]           = useState('');
  const [matches, setMatches]       = useState([]);
  const [searching, setSearching]   = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating]     = useState(false);
  const [newName, setNewName]       = useState('');
  const [newAliases, setNewAliases] = useState('');
  const debounceRef                 = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setMatches([]);
      setShowCreate(false);
      setNewName('');
      setNewAliases('');
      setSearching(false);
    } else if (prefillName) {
      setQuery(prefillName);
      setNewName(prefillName);
      runMatch(prefillName);
    }
  }, [open]);

  const runMatch = useCallback(async (q) => {
    if (!q.trim() || q.trim().length < 2) {
      setMatches([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const res = await api.get('/organisations/match', { params: { q: q.trim() } });
      setMatches(res.data || []);
    } catch {
      setMatches([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleQueryChange = (val) => {
    setQuery(val);
    setShowCreate(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runMatch(val), 150);
  };

  const handleUseExisting = (org) => {
    onSelect({ id: org.id, name: org.name });
    onOpenChange(false);
  };

  const handleCreate = async () => {
    const name = newName.trim() || query.trim();
    if (!name) return;
    setCreating(true);
    try {
      const aliases = newAliases.split(',').map(a => a.trim()).filter(Boolean);
      const res = await api.post('/organisations', { name, aliases });
      if (setOrganisations) setOrganisations(prev => [...prev, res.data]);
      onSelect({ id: res.data.id, name: res.data.name });
      toast.success(`Organisation "${name}" created`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create organisation');
    } finally {
      setCreating(false);
    }
  };

  const hasMatches = matches.length > 0;
  const queryLong  = query.trim().length >= 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Find or Add Organisation</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9C8E84]" size={15} />
            <Input
              autoFocus
              value={query}
              onChange={e => handleQueryChange(e.target.value)}
              placeholder="Type organisation name..."
              className="pl-9 bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
            />
            {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9C8E84] animate-spin" />}
          </div>

          {queryLong && !searching && (
            <div className="space-y-2">
              {hasMatches ? (
                <>
                  <p className="text-xs text-[#6E6258] font-medium uppercase tracking-wide">
                    {matches.length} existing record{matches.length !== 1 ? 's' : ''} found
                  </p>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {matches.map(m => {
                      const badge = confidenceBadge(m.confidence, m.match_type);
                      return (
                        <div key={m.id} className="flex items-center justify-between gap-3 p-2.5 bg-[#FFF4E8] border border-[#E7D2B8] rounded-lg">
                          <div className="flex items-start gap-2 min-w-0">
                            {confidenceIcon(m.confidence, m.match_type)}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{m.name}</p>
                              {m.aliases?.length > 0 && (
                                <p className="text-xs text-[#9C8E84] truncate">Also known as: {m.aliases.join(', ')}</p>
                              )}
                              {m.canonical_name && m.canonical_name !== m.name && (
                                <p className="text-xs text-[#9C8E84]">Canonical: {m.canonical_name}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className={`text-[10px] px-1.5 ${badge.cls}`}>{badge.label}</Badge>
                            <Button
                              size="sm"
                              onClick={() => handleUseExisting(m)}
                              className="h-7 px-3 text-xs bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
                            >
                              Use
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="pt-1 border-t border-border">
                    <button
                      onClick={() => { setShowCreate(true); setNewName(query.trim()); }}
                      className="text-xs text-[#6E6258] hover:text-foreground transition-colors flex items-center gap-1"
                    >
                      <Plus size={12} /> None of these — create new record
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-3">
                  <p className="text-sm text-muted-foreground mb-2">No existing organisations found for "{query}"</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setShowCreate(true); setNewName(query.trim()); }}
                    className="border-border text-foreground hover:bg-[#FFF4E8] gap-1.5"
                  >
                    <Plus size={14} /> Create "{query.trim()}"
                  </Button>
                </div>
              )}
            </div>
          )}

          {showCreate && (
            <div className="space-y-3 pt-2 border-t border-border">
              <p className="text-xs font-medium text-[#6E6258] uppercase tracking-wide">Create new organisation</p>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Name *</label>
                <Input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="Full organisation name"
                  className="bg-white text-[#1F1A17] border-border"
                />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">
                  Aliases <span className="text-[#9C8E84]">(comma separated, e.g. FCA, UK FCA)</span>
                </label>
                <Input
                  value={newAliases}
                  onChange={e => setNewAliases(e.target.value)}
                  placeholder="e.g. FCA, UK FCA, UK Financial Conduct Authority"
                  className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleCreate}
                  disabled={!newName.trim() || creating}
                  className="flex-1 bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
                >
                  {creating ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
                  {creating ? 'Creating...' : 'Create Organisation'}
                </Button>
                <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─── Person Match Dialog ───

export function PersonMatchDialog({ open, onOpenChange, onSelect, organisations, persons, setPersons, prefillName = '' }) {
  const [query, setQuery]           = useState('');
  const [matches, setMatches]       = useState([]);
  const [searching, setSearching]   = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating]     = useState(false);
  const [newName, setNewName]       = useState('');
  const [newTitle, setNewTitle]     = useState('');
  const [newOrgId, setNewOrgId]     = useState('');
  const [newAliases, setNewAliases] = useState('');
  const debounceRef                 = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setMatches([]);
      setShowCreate(false);
      setNewName('');
      setNewTitle('');
      setNewOrgId('');
      setNewAliases('');
      setSearching(false);
    } else if (prefillName) {
      setQuery(prefillName);
      setNewName(prefillName);
      runMatch(prefillName);
    }
  }, [open]);

  const runMatch = useCallback(async (q) => {
    if (!q.trim() || q.trim().length < 2) {
      setMatches([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const res = await api.get('/persons/match', { params: { q: q.trim() } });
      setMatches(res.data || []);
    } catch {
      setMatches([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleQueryChange = (val) => {
    setQuery(val);
    setShowCreate(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runMatch(val), 150);
  };

  const handleUseExisting = (person) => {
    onSelect({ id: person.id, name: person.name });
    onOpenChange(false);
  };

  const handleCreate = async () => {
    const name = newName.trim() || query.trim();
    if (!name) return;
    setCreating(true);
    try {
      const aliases = newAliases.split(',').map(a => a.trim()).filter(Boolean);
      const res = await api.post('/persons', {
        name,
        title: newTitle.trim(),
        organisation_id: newOrgId || null,
        aliases,
      });
      if (setPersons) setPersons(prev => [...prev, res.data]);
      onSelect({ id: res.data.id, name: res.data.name });
      toast.success(`Person "${name}" created`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create person');
    } finally {
      setCreating(false);
    }
  };

  const hasMatches = matches.length > 0;
  const queryLong  = query.trim().length >= 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Find or Add Person</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9C8E84]" size={15} />
            <Input
              autoFocus
              value={query}
              onChange={e => handleQueryChange(e.target.value)}
              placeholder="Type person name..."
              className="pl-9 bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
            />
            {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9C8E84] animate-spin" />}
          </div>

          {queryLong && !searching && (
            <div className="space-y-2">
              {hasMatches ? (
                <>
                  <p className="text-xs text-[#6E6258] font-medium uppercase tracking-wide">
                    {matches.length} existing record{matches.length !== 1 ? 's' : ''} found
                  </p>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {matches.map(m => {
                      const badge = confidenceBadge(m.confidence, m.match_type);
                      return (
                        <div key={m.id} className="flex items-center justify-between gap-3 p-2.5 bg-[#FFF4E8] border border-[#E7D2B8] rounded-lg">
                          <div className="flex items-start gap-2 min-w-0">
                            {confidenceIcon(m.confidence, m.match_type)}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{m.name}</p>
                              {m.title && <p className="text-xs text-[#9C8E84]">{m.title}</p>}
                              {m.aliases?.length > 0 && (
                                <p className="text-xs text-[#9C8E84] truncate">Also known as: {m.aliases.join(', ')}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className={`text-[10px] px-1.5 ${badge.cls}`}>{badge.label}</Badge>
                            <Button
                              size="sm"
                              onClick={() => handleUseExisting(m)}
                              className="h-7 px-3 text-xs bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
                            >
                              Use
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="pt-1 border-t border-border">
                    <button
                      onClick={() => { setShowCreate(true); setNewName(query.trim()); }}
                      className="text-xs text-[#6E6258] hover:text-foreground transition-colors flex items-center gap-1"
                    >
                      <Plus size={12} /> None of these — create new record
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-3">
                  <p className="text-sm text-muted-foreground mb-2">No existing persons found for "{query}"</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setShowCreate(true); setNewName(query.trim()); }}
                    className="border-border text-foreground hover:bg-[#FFF4E8] gap-1.5"
                  >
                    <Plus size={14} /> Create "{query.trim()}"
                  </Button>
                </div>
              )}
            </div>
          )}

          {showCreate && (
            <div className="space-y-3 pt-2 border-t border-border">
              <p className="text-xs font-medium text-[#6E6258] uppercase tracking-wide">Create new person</p>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Name *</label>
                <Input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="Full name"
                  className="bg-white text-[#1F1A17] border-border"
                />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Title</label>
                <Input
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                  placeholder="Job title"
                  className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Organisation</label>
                <Select value={newOrgId} onValueChange={setNewOrgId}>
                  <SelectTrigger className="bg-white text-[#1F1A17] border-border">
                    <SelectValue placeholder="Select organisation (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-border">
                    {(organisations || []).map(o => (
                      <SelectItem key={o.id} value={o.id} className="text-[#1F1A17]">{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">
                  Aliases <span className="text-[#9C8E84]">(comma separated)</span>
                </label>
                <Input
                  value={newAliases}
                  onChange={e => setNewAliases(e.target.value)}
                  placeholder="e.g. J. Smith, Johnny"
                  className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleCreate}
                  disabled={!newName.trim() || creating}
                  className="flex-1 bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
                >
                  {creating ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
                  {creating ? 'Creating...' : 'Create Person'}
                </Button>
                <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─── Location Match Dialog ───

export function LocationMatchDialog({ open, onOpenChange, onSelect, setLocations, publicationId, prefillName = '' }) {
  const [query, setQuery]           = useState('');
  const [matches, setMatches]       = useState([]);
  const [searching, setSearching]   = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating]     = useState(false);
  const [newAreaName, setNewAreaName] = useState('');
  const [newHubName, setNewHubName]   = useState('');
  const [newAliases, setNewAliases]   = useState('');
  const debounceRef                   = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setMatches([]);
      setShowCreate(false);
      setNewAreaName('');
      setNewHubName('');
      setNewAliases('');
      setSearching(false);
    } else if (prefillName) {
      setQuery(prefillName);
      setNewAreaName(prefillName);
      runMatch(prefillName);
    }
  }, [open]);

  const runMatch = useCallback(async (q) => {
    if (!q.trim() || q.trim().length < 2) {
      setMatches([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const params = { q: q.trim() };
      if (publicationId) params.publication_id = publicationId;
      const res = await api.get('/locations/match', { params });
      setMatches(res.data || []);
    } catch {
      setMatches([]);
    } finally {
      setSearching(false);
    }
  }, [publicationId]);

  const handleQueryChange = (val) => {
    setQuery(val);
    setShowCreate(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runMatch(val), 150);
  };

  const handleUseExisting = (loc) => {
    onSelect({ id: loc.id, area_name: loc.area_name, hub_name: loc.hub_name });
    onOpenChange(false);
  };

  const handleCreate = async () => {
    const area = newAreaName.trim() || query.trim();
    if (!area) return;
    setCreating(true);
    try {
      const aliases = newAliases.split(',').map(a => a.trim()).filter(Boolean);
      const res = await api.post('/locations', {
        area_name: area,
        hub_name: newHubName.trim(),
        aliases,
        publication_id: publicationId || null,
      });
      if (setLocations) setLocations(prev => [...prev, res.data]);
      onSelect({ id: res.data.id, area_name: res.data.area_name, hub_name: res.data.hub_name });
      toast.success(`Location "${area}" created`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create location');
    } finally {
      setCreating(false);
    }
  };

  const hasMatches = matches.length > 0;
  const queryLong  = query.trim().length >= 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Find or Add Location</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9C8E84]" size={15} />
            <Input
              autoFocus
              value={query}
              onChange={e => handleQueryChange(e.target.value)}
              placeholder="Type area name (e.g. Wokingham, Bracknell...)"
              className="pl-9 bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
            />
            {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9C8E84] animate-spin" />}
          </div>

          {queryLong && !searching && (
            <div className="space-y-2">
              {hasMatches ? (
                <>
                  <p className="text-xs text-[#6E6258] font-medium uppercase tracking-wide">
                    {matches.length} existing record{matches.length !== 1 ? 's' : ''} found
                  </p>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {matches.map(m => {
                      const badge = confidenceBadge(m.confidence, m.match_type);
                      return (
                        <div key={m.id} className="flex items-center justify-between gap-3 p-2.5 bg-[#FFF4E8] border border-[#E7D2B8] rounded-lg">
                          <div className="flex items-start gap-2 min-w-0">
                            {confidenceIcon(m.confidence, m.match_type)}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{m.area_name}</p>
                              {m.hub_name && (
                                <p className="text-xs text-[#9C8E84]">Hub: {m.hub_name}</p>
                              )}
                              {m.aliases?.length > 0 && (
                                <p className="text-xs text-[#9C8E84] truncate">Also known as: {m.aliases.join(', ')}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className={`text-[10px] px-1.5 ${badge.cls}`}>{badge.label}</Badge>
                            <Button
                              size="sm"
                              onClick={() => handleUseExisting(m)}
                              className="h-7 px-3 text-xs bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
                            >
                              Use
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="pt-1 border-t border-border">
                    <button
                      onClick={() => { setShowCreate(true); setNewAreaName(query.trim()); }}
                      className="text-xs text-[#6E6258] hover:text-foreground transition-colors flex items-center gap-1"
                    >
                      <Plus size={12} /> None of these — create new record
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-3">
                  <p className="text-sm text-muted-foreground mb-2">No existing locations found for "{query}"</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setShowCreate(true); setNewAreaName(query.trim()); }}
                    className="border-border text-foreground hover:bg-[#FFF4E8] gap-1.5"
                  >
                    <Plus size={14} /> Create "{query.trim()}"
                  </Button>
                </div>
              )}
            </div>
          )}

          {showCreate && (
            <div className="space-y-3 pt-2 border-t border-border">
              <p className="text-xs font-medium text-[#6E6258] uppercase tracking-wide">Create new location</p>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Area Name *</label>
                <Input
                  value={newAreaName}
                  onChange={e => setNewAreaName(e.target.value)}
                  placeholder="e.g. Wokingham"
                  className="bg-white text-[#1F1A17] border-border"
                />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Hub Name</label>
                <Input
                  value={newHubName}
                  onChange={e => setNewHubName(e.target.value)}
                  placeholder="e.g. Reading"
                  className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">
                  Aliases <span className="text-[#9C8E84]">(comma separated)</span>
                </label>
                <Input
                  value={newAliases}
                  onChange={e => setNewAliases(e.target.value)}
                  placeholder="e.g. Wokingham Borough, WBC"
                  className="bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84]"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleCreate}
                  disabled={!newAreaName.trim() || creating}
                  className="flex-1 bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
                >
                  {creating ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
                  {creating ? 'Creating...' : 'Create Location'}
                </Button>
                <Button variant="ghost" onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}